import { useEffect, useRef } from "react";

export default function HeroCanvas() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    const ctx = canvas.getContext("2d");
    let raf, w, h;

    const t = (1 + Math.sqrt(5)) / 2;
    const raw = [
      [-1, t, 0], [1, t, 0], [-1, -t, 0], [1, -t, 0],
      [0, -1, t], [0, 1, t], [0, -1, -t], [0, 1, -t],
      [t, 0, -1], [t, 0, 1], [-t, 0, -1], [-t, 0, 1],
    ];
    const pts = raw.map((v) => {
      const l = Math.hypot(v[0], v[1], v[2]);
      return [v[0] / l, v[1] / l, v[2] / l];
    });
    const edges = [];
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const d = Math.hypot(pts[i][0] - pts[j][0], pts[i][1] - pts[j][1], pts[i][2] - pts[j][2]);
        if (d < 1.2) edges.push([i, j]);
      }
    }

    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    const onMove = (e) => {
      const r = wrap.getBoundingClientRect();
      mouse.tx = ((e.clientX - r.left) / r.width - 0.5) * 0.9;
      mouse.ty = ((e.clientY - r.top) / r.height - 0.5) * 0.9;
    };
    const onLeave = () => {
      mouse.tx = 0;
      mouse.ty = 0;
    };
    wrap.addEventListener("mousemove", onMove);
    wrap.addEventListener("mouseleave", onLeave);

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = wrap.clientWidth;
      h = wrap.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    const draw = (time) => {
      const ts = time / 1000;
      mouse.x += (mouse.tx - mouse.x) * 0.05;
      mouse.y += (mouse.ty - mouse.y) * 0.05;
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2;
      const cy = h / 2;
      const R = Math.min(w, h) * 0.3;
      const rx = ts * 0.16 + mouse.y;
      const ry = ts * 0.24 + mouse.x;

      const proj = pts.map((p) => {
        const [x, y, z] = p;
        const x1 = x * Math.cos(ry) + z * Math.sin(ry);
        const z1 = -x * Math.sin(ry) + z * Math.cos(ry);
        const y1 = y * Math.cos(rx) - z1 * Math.sin(rx);
        const z2 = y * Math.sin(rx) + z1 * Math.cos(rx);
        const f = 3.2 / (3.2 + z2);
        return [cx + x1 * R * f, cy + y1 * R * f, z2];
      });

      edges.forEach(([i, j]) => {
        const [x1, y1, z1] = proj[i];
        const [x2, y2, z2] = proj[j];
        const depth = (z1 + z2) / 2;
        ctx.strokeStyle = `rgba(15,20,26,${(0.55 - depth * 0.35) * 0.38})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      });

      proj.forEach(([x, y, z], i) => {
        const hot = i % 4 === 0;
        if (hot) {
          ctx.beginPath();
          ctx.arc(x, y, 3, 0, Math.PI * 2);
          ctx.fillStyle = "#EB5E28";
          ctx.fill();
          const pulse = 5 + Math.sin(ts * 2 + i) * 1.6;
          ctx.beginPath();
          ctx.arc(x, y, pulse, 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(235,94,40,0.35)";
          ctx.lineWidth = 1;
          ctx.stroke();
        } else {
          ctx.beginPath();
          ctx.arc(x, y, 2.4, 0, Math.PI * 2);
          ctx.fillStyle = "#FFFFFF";
          ctx.fill();
          ctx.strokeStyle = `rgba(0,139,155,${0.9 - z * 0.3})`;
          ctx.lineWidth = 1.2;
          ctx.stroke();
        }
      });

      ctx.beginPath();
      ctx.ellipse(cx, cy, R * 1.6, R * 0.5, Math.sin(ts * 0.1) * 0.2, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(0,139,155,0.3)";
      ctx.setLineDash([2, 6]);
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.setLineDash([]);

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      wrap.removeEventListener("mousemove", onMove);
      wrap.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div ref={wrapRef} data-testid="hero-3d-interactive-canvas" className="relative h-full w-full cursor-crosshair">
      <canvas ref={canvasRef} className="h-full w-full" />
    </div>
  );
}
